/**
 * Audio-capture constants + pure browser-capability helpers for voice ordering
 * (KAN-143). Kept out of the recorder hook so the branching logic - which mime
 * type to record in, how to read a `getUserMedia` failure - is unit-testable
 * without a real `MediaRecorder`.
 */

/** Hard cap on a single recording. A salon reads a short shopping list. */
export const VOICE_ORDER_MAX_DURATION_MS = 60_000;

/**
 * Mono, voice-optimized bitrate. Explicitly set so a full 60s clip stays around
 * 240KB - comfortably under the API's upload limits with no infra dependency
 * (see KAN-145). Passed to `MediaRecorder` as `audioBitsPerSecond`.
 */
export const VOICE_ORDER_AUDIO_BITS_PER_SECOND = 32_000;

/**
 * Preferred container mime types, in order. Safari's `MediaRecorder` does not
 * support `audio/webm` and throws if you hand it that, so we feature-detect and
 * fall back to `audio/mp4`; the parse endpoint (Deepgram) accepts both.
 */
export const VOICE_ORDER_MIME_CANDIDATES = ["audio/webm", "audio/mp4"] as const;

/**
 * The classes of recorder failure the UI has a distinct message + recovery for.
 * `permission-denied` is the single most likely on-stage demo failure, so it is
 * never collapsed into a generic error.
 */
export type RecorderErrorKind =
  | "permission-denied"
  | "no-microphone"
  | "recording-unavailable"
  | "unsupported"
  | "unknown";

/**
 * Whether this browser can record at all. Guards the recorder before it ever
 * calls `getUserMedia`, so an unsupported browser gets the `unsupported`
 * message rather than an opaque throw.
 */
export function isRecordingSupported(): boolean {
  return (
    typeof navigator !== "undefined" &&
    typeof navigator.mediaDevices?.getUserMedia === "function" &&
    typeof MediaRecorder !== "undefined"
  );
}

/**
 * The first supported preferred mime type, or `undefined` to let
 * `MediaRecorder` choose its own default (still valid - the actual type is read
 * back off the produced blob before upload).
 */
export function pickAudioMimeType(): string | undefined {
  if (
    typeof MediaRecorder === "undefined" ||
    typeof MediaRecorder.isTypeSupported !== "function"
  ) {
    return undefined;
  }
  return VOICE_ORDER_MIME_CANDIDATES.find((type) =>
    MediaRecorder.isTypeSupported(type)
  );
}

/**
 * Map a `getUserMedia` rejection to a `RecorderErrorKind`. The browser reports
 * these as a `DOMException` whose `name` carries the reason; a dismissed or
 * denied permission prompt is `NotAllowedError`, which the UI must surface with
 * a retry rather than swallow.
 */
export function classifyGetUserMediaError(error: unknown): RecorderErrorKind {
  const name =
    error instanceof Error ? error.name : (error as { name?: string })?.name;

  switch (name) {
    case "NotAllowedError":
    case "SecurityError":
    case "PermissionDeniedError":
      return "permission-denied";
    case "NotFoundError":
    case "DevicesNotFoundError":
    case "OverconstrainedError":
    case "ConstraintNotSatisfiedError":
      return "no-microphone";
    case "NotReadableError":
    case "TrackStartError":
    case "AbortError":
      return "recording-unavailable";
    default:
      return "unknown";
  }
}
